import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Select, MenuItem, Table, TableBody, TableCell, TableHead, TableRow,
  Stack, Typography, Alert, IconButton, Autocomplete, Box, Link, Switch, Tooltip
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, Delete as DeleteIcon, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import bomService from '../../services/bomService';
import api from '../../config/api';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// SKU -> product-name map for the brand (includes parts without COGS and
// inactive products, so name resolution has no holes). Rishabh 2026-07-03:
// the BOM edit screen must show names, not bare SKUs.
const fetchProductNames = async (brandId) => {
  const response = await api.get(`${API_BASE}/api/cogs/brands/${brandId}/product-names`);
  return response.data;
};

const FALLBACK_UOM_OPTIONS = [
  { value: 'units', label: 'units' }, { value: 'ml', label: 'ml' }, { value: 'l', label: 'l' },
  { value: 'mg', label: 'mg' }, { value: 'g', label: 'g' }, { value: 'kg', label: 'kg' },
  { value: 'mm', label: 'mm' }, { value: 'cm', label: 'cm' }, { value: 'm', label: 'm' },
  { value: 'yards', label: 'yards' },
];

export const EMPTY_COMPONENT_ROW = { sku: '', quantity: '', unit: 'units', notes: '', is_active: 1 };

/**
 * Shared create/edit dialog for PRODUCTION BOMs (contract-manufacturing
 * recipes). Used by the BOM catalog ("New Production BOM") and the Products &
 * COGS product page BOM tab (Edit). Saves via /api/boms production-boms
 * endpoints — full-replacement semantics: saving replaces ALL production
 * alternatives for the assembly with this single master recipe.
 *
 * Props:
 *   open, onClose
 *   brandId
 *   mode            — 'create' | 'edit'
 *   initialForm     — { assembly_sku, name, production_lead_time_days, components: [{sku,quantity,unit,notes}] }
 *   lockAssembly    — disable the assembly SKU field (edit mode / prefilled create)
 *   allowDeactivate — show a Deactivate button in edit mode
 *   onSaved(assemblySku), onDeactivated(assemblySku)
 */
export default function ProductionBomEditorDialog({
  open, onClose, brandId, mode = 'create', initialForm = null,
  lockAssembly = false, allowDeactivate = false, onSaved, onDeactivated,
}) {
  const [form, setForm] = useState({ assembly_sku: '', name: '', production_lead_time_days: '', components: [{ ...EMPTY_COMPONENT_ROW }] });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [uomOptions, setUomOptions] = useState(FALLBACK_UOM_OPTIONS);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    bomService.getUomOptions()
      .then((d) => { if (Array.isArray(d?.options) && d.options.length) setUomOptions(d.options); })
      .catch(() => {});
  }, []);

  // Name resolution + search options. Loaded per brand when the dialog opens;
  // failure is non-fatal (fields degrade to bare SKU entry).
  useEffect(() => {
    if (!open || !brandId) return;
    fetchProductNames(brandId)
      .then((d) => { if (Array.isArray(d?.products)) setProducts(d.products); })
      .catch(() => {});
  }, [open, brandId]);

  const nameBySku = useMemo(() => {
    const m = new Map();
    for (const p of products) m.set(String(p.sku).toLowerCase().trim(), p.name || '');
    return m;
  }, [products]);
  const resolveName = (sku) => {
    const key = String(sku || '').toLowerCase().trim();
    if (!key) return null;
    return nameBySku.has(key) ? (nameBySku.get(key) || '(no name on file)') : undefined;
  };

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm({
      assembly_sku: initialForm?.assembly_sku || '',
      name: initialForm?.name || '',
      production_lead_time_days: initialForm?.production_lead_time_days || '',
      components: (initialForm?.components && initialForm.components.length)
        ? initialForm.components.map((c) => ({
          sku: c.sku || c.component_sku || '',
          quantity: String(c.quantity ?? ''),
          unit: c.unit || 'units',
          notes: c.notes || '',
          is_active: c.is_active === 0 ? 0 : 1,
        }))
        : [{ ...EMPTY_COMPONENT_ROW }],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setComponentField = (idx, field, value) => {
    setForm((f) => {
      const components = f.components.slice();
      components[idx] = { ...components[idx], [field]: value };
      return { ...f, components };
    });
  };

  const save = async () => {
    if (!brandId) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        assembly_sku: form.assembly_sku.trim(),
        name: form.name.trim(),
        production_lead_time_days: parseInt(form.production_lead_time_days, 10) || 0,
        components: form.components
          .filter((c) => c.sku.trim() !== '' || String(c.quantity).trim() !== '')
          .map((c) => ({ sku: c.sku.trim(), quantity: Number(c.quantity), unit: c.unit, notes: c.notes, is_active: c.is_active === 0 ? 0 : 1 })),
      };
      if (mode === 'create') {
        await bomService.createProductionBom(brandId, payload);
      } else {
        await bomService.updateProductionBom(brandId, payload.assembly_sku, payload);
      }
      if (onSaved) onSaved(payload.assembly_sku);
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    if (!brandId || !form.assembly_sku) return;
    if (!window.confirm(`Deactivate the production BOM for ${form.assembly_sku}? Manufacture POs for this SKU will stop working until a new BOM is created.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await bomService.deleteProductionBom(brandId, form.assembly_sku.trim());
      if (onDeactivated) onDeactivated(form.assembly_sku.trim());
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e, 'Deactivate failed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'New Production BOM' : `Edit Production BOM: ${form.assembly_sku}`}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, mt: 1 }}>
          <TextField
            size="small" label="Assembly SKU (L1 finished good)" required
            value={form.assembly_sku}
            disabled={mode === 'edit' || lockAssembly}
            onChange={(e) => setForm((f) => ({ ...f, assembly_sku: e.target.value }))}
            sx={{ minWidth: 260 }}
            helperText={resolveName(form.assembly_sku) || ' '}
            FormHelperTextProps={{ sx: { mx: 0.5, fontSize: 11 } }}
          />
          <TextField
            size="small" label="Name (optional)"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ flexGrow: 1 }}
          />
          <TextField
            size="small" label="Lead time (days)" type="number"
            value={form.production_lead_time_days}
            onChange={(e) => setForm((f) => ({ ...f, production_lead_time_days: e.target.value }))}
            sx={{ width: 140 }}
          />
        </Stack>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Components (per 1 unit of the assembly)</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Component SKU</TableCell>
              <TableCell align="right" sx={{ width: 130 }}>Quantity</TableCell>
              <TableCell sx={{ width: 110 }}>Unit</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="center" sx={{ width: 96 }}>
                Active
                <Tooltip title="Toggle a component OFF to skip it in production (it stays in the recipe so you can turn it back on later); ON to consume it again.">
                  <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {form.components.map((c, idx) => (
              <TableRow key={idx} sx={{ opacity: c.is_active === 0 ? 0.5 : 1 }}>
                <TableCell>
                  <Autocomplete
                    size="small" fullWidth freeSolo
                    options={products}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : o.sku)}
                    filterOptions={(opts, state) => {
                      const q = state.inputValue.trim().toLowerCase();
                      if (!q) return opts.slice(0, 50);
                      return opts.filter((o) =>
                        String(o.sku).toLowerCase().includes(q)
                        || String(o.name || '').toLowerCase().includes(q)
                      ).slice(0, 50);
                    }}
                    renderOption={(props, o) => (
                      <Box component="li" {...props} key={o.sku}>
                        <Box>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{o.sku}</Typography>
                          <Typography variant="caption" color="text.secondary">{o.name || '(no name on file)'}</Typography>
                        </Box>
                      </Box>
                    )}
                    inputValue={c.sku}
                    onInputChange={(e, v) => setComponentField(idx, 'sku', v || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params} placeholder="SKU or name…"
                        inputProps={{ ...params.inputProps, style: { fontFamily: 'monospace' } }}
                        helperText={(() => {
                          const n = resolveName(c.sku);
                          if (n === null) return ' ';
                          if (n === undefined) return nameBySku.size ? 'SKU not found for this brand' : ' ';
                          return n;
                        })()}
                        FormHelperTextProps={{
                          sx: {
                            mx: 0.5, fontSize: 11,
                            color: resolveName(c.sku) === undefined && nameBySku.size ? 'warning.main' : 'text.secondary'
                          }
                        }}
                      />
                    )}
                  />
                  {/* Recipes hold QUANTITIES only — a component's cost lives on
                      the component itself (manual/PO precedence), so recipes
                      always reflect the live cost. This link answers "how do I
                      change the cost from here" (Rishabh 2026-07-06). */}
                  {typeof resolveName(c.sku) === 'string' && (
                    <Typography variant="caption" sx={{ mx: 0.5 }}>
                      <Link
                        href={`/inventory/cogs?product=${encodeURIComponent(String(c.sku).trim())}&ptab=1`}
                        target="_blank" rel="noopener" underline="hover"
                      >
                        Edit this component's cost ↗
                      </Link>
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                  <TextField
                    size="small" type="number" fullWidth
                    value={c.quantity}
                    onChange={(e) => setComponentField(idx, 'quantity', e.target.value)}
                    inputProps={{ min: 0, step: 'any' }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Select
                    size="small" fullWidth value={c.unit}
                    onChange={(e) => setComponentField(idx, 'unit', e.target.value)}
                  >
                    {uomOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    size="small" fullWidth placeholder="optional"
                    value={c.notes}
                    onChange={(e) => setComponentField(idx, 'notes', e.target.value)}
                  />
                </TableCell>
                <TableCell align="center" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                  <Tooltip title={c.is_active === 0
                    ? 'Inactive — kept in the recipe but NOT consumed in production (skipped by the build/cost). Toggle on to use it again.'
                    : 'Active — consumed in production. Toggle off to skip it for now; it stays in the recipe so you can turn it back on later.'}>
                    <Switch
                      size="small"
                      checked={c.is_active !== 0}
                      inputProps={{ 'aria-label': c.is_active === 0 ? 'Component inactive — switch on to activate' : 'Component active — switch off to deactivate' }}
                      onChange={(e) => setComponentField(idx, 'is_active', e.target.checked ? 1 : 0)}
                    />
                  </Tooltip>
                  <IconButton size="small" onClick={() => setForm((f) => ({ ...f, components: f.components.filter((_, i) => i !== idx) }))} disabled={form.components.length <= 1}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setForm((f) => ({ ...f, components: [...f.components, { ...EMPTY_COMPONENT_ROW }] }))} sx={{ mt: 1 }}>
          Add component
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Quantities may use measures (e.g. 30 ml, 0.5 kg) when the component's stock is kept in a
          compatible unit — set a product's stock unit on the Products &amp; COGS page.
          {mode === 'edit' ? ' Saving replaces all production alternatives for this assembly with this single master recipe.' : ''}
        </Typography>
      </DialogContent>
      <DialogActions>
        {mode === 'edit' && allowDeactivate && (
          <Button color="error" startIcon={<DeleteIcon />} onClick={deactivate} disabled={saving || deleting} sx={{ mr: 'auto' }}>
            {deleting ? 'Deactivating…' : 'Deactivate BOM'}
          </Button>
        )}
        <Button onClick={onClose} disabled={saving || deleting}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || deleting}>
          {saving ? 'Saving…' : (mode === 'create' ? 'Create BOM' : 'Save changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
